import {
  payoutQuickStats,
} from "../payoutData";
import type { PayoutAccount, RecentPayout } from "../payoutData";
import { payoutBankLogoFor } from "./payoutBankLogos";
import { payoutApi, toRupeesFromWire } from "./payoutApiClient";
import { availableBalancePaiseFromWire, paiseToRupees } from "./payoutMoney";

export type PayoutDashboardViewData = {
  accounts: PayoutAccount[];
  recentPayouts: RecentPayout[];
  totalAvailableBalance: number;
  quickStats: typeof payoutQuickStats;
};

export const emptyDashboardViewData: PayoutDashboardViewData = {
  accounts: [],
  recentPayouts: [],
  totalAvailableBalance: 0,
  quickStats: payoutQuickStats.map((stat, index) => ({
    ...stat,
    value: index === 3 ? "0" : "\u20B90",
  })),
};

const pickString = (
  record: Record<string, unknown>,
  keys: string[],
  fallback = "",
) => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value;
    if (typeof value === "number") return String(value);
  }
  return fallback;
};

const pickNumber = (
  record: Record<string, unknown>,
  keys: string[],
  fallback = 0,
  moneyOnWire = false,
) => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number") return moneyOnWire ? toRupeesFromWire(value) : value;
    if (typeof value === "string" && value.trim() && !Number.isNaN(Number(value))) {
      const parsed = Number(value);
      return moneyOnWire ? toRupeesFromWire(parsed) : parsed;
    }
  }
  return fallback;
};

const formatDashboardMoney = (amount: number) =>
  `\u20B9${amount.toLocaleString("en-IN", {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  })}`;

const accountFromApi = (
  account: Record<string, unknown>,
  fallback: PayoutAccount,
): PayoutAccount => {
  const accountNumber = pickString(
    account,
    ["account_number", "accountNumber", "masked_account_number", "maskedAccountNumber", "account_number_last4", "accountNumberLast4", "last4"],
    fallback.accountNumber,
  );

  const id = pickString(account, ["id", "account_id", "accountId", "funding_account_id", "fundingAccountId"], fallback.id);
  const name = pickString(account, ["name", "account_name", "accountName", "display_name", "displayName", "label"], fallback.name);
  const bank = pickString(account, ["bank", "bank_name", "bankName"], fallback.bank);
  const ifsc = pickString(account, ["ifsc", "ifsc_code", "ifscCode"], fallback.ifsc);
  const logoUrl = pickString(account, ["logo_url", "logoUrl", "bank_logo_url", "bankLogoUrl"], fallback.logoUrl);

  return {
    id,
    name,
    school: pickString(account, ["school", "school_name", "schoolName"], fallback.school),
    bank,
    logoUrl: payoutBankLogoFor({ id, name, bank, ifsc, logoUrl }),
    accountNumber,
    ifsc,
    currency: pickString(account, ["currency"], fallback.currency),
    addedOn: pickString(account, ["added_on", "addedOn", "created_at", "createdAt"], fallback.addedOn),
    availableBalance: pickNumber(
      account,
      ["available_balance_paise", "availableBalancePaise", "available_balance", "availableBalance"],
      fallback.availableBalance,
      Boolean(account.available_balance_paise || account.availableBalancePaise),
    ),
    accountBalance: pickNumber(
      account,
      ["account_balance_paise", "accountBalancePaise", "account_balance", "accountBalance"],
      fallback.accountBalance,
      Boolean(account.account_balance_paise || account.accountBalancePaise),
    ),
    fundsOnHold: pickNumber(
      account,
      ["funds_on_hold_paise", "fundsOnHoldPaise", "funds_on_hold", "fundsOnHold"],
      fallback.fundsOnHold,
      Boolean(account.funds_on_hold_paise || account.fundsOnHoldPaise),
    ),
    overdraftBalance: pickNumber(
      account,
      ["overdraft_balance_paise", "overdraftBalancePaise", "overdraft_balance", "overdraftBalance"],
      fallback.overdraftBalance,
      Boolean(account.overdraft_balance_paise || account.overdraftBalancePaise),
    ),
    overdraftLimit: pickNumber(
      account,
      ["overdraft_limit_paise", "overdraftLimitPaise", "overdraft_limit", "overdraftLimit"],
      fallback.overdraftLimit,
      Boolean(account.overdraft_limit_paise || account.overdraftLimitPaise),
    ),
    lowBalanceThreshold: pickNumber(
      account,
      ["low_balance_threshold_paise", "lowBalanceThresholdPaise", "low_balance_threshold", "lowBalanceThreshold"],
      fallback.lowBalanceThreshold,
      Boolean(account.low_balance_threshold_paise || account.lowBalanceThresholdPaise),
    ),
    accent: fallback.accent,
  };
};

const payoutFromApi = (
  payment: Record<string, unknown>,
  fallback: RecentPayout,
): RecentPayout => ({
  referenceId: pickString(payment, ["payment_id", "paymentId", "reference_id", "referenceId", "payment_reference", "paymentReference", "id"], fallback.referenceId),
  utrNumber: pickString(payment, ["utr", "utr_number", "utrNumber", "bank_reference_number", "bankReferenceNumber"], fallback.utrNumber),
  beneficiary: pickString(payment, ["name_entered", "nameEntered", "beneficiary_name", "beneficiaryName", "beneficiary"], fallback.beneficiary),
  account: pickString(payment, ["funding_account_name", "fundingAccountName", "account_name", "accountName", "payout_account_name", "payoutAccountName"], fallback.account),
  accountId: pickString(payment, ["funding_account_id", "fundingAccountId", "account_id", "accountId", "payout_account_id", "payoutAccountId"], fallback.accountId),
  purpose: pickString(payment, ["purpose", "narration", "description"], fallback.purpose),
  amount: pickNumber(
    payment,
    ["amount_paise", "amountPaise", "amount", "transfer_amount_paise", "transferAmountPaise"],
    fallback.amount,
    Boolean(payment.amount_paise || payment.amountPaise || payment.transfer_amount_paise || payment.transferAmountPaise),
  ),
  status: pickString(payment, ["status", "state"], fallback.status),
  settlementTime: pickString(payment, ["settlement_time", "settlementTime", "settled_at", "settledAt", "created_at", "createdAt"], fallback.settlementTime),
});

const summaryQuickStats = (
  summary: Record<string, unknown>,
): typeof payoutQuickStats => [
  {
    label: payoutQuickStats[0].label,
    value: formatDashboardMoney(pickNumber(
      summary,
      ["todays_payouts_paise", "todaysPayoutsPaise", "today_payouts_paise", "todayPayoutsPaise", "today_payouts", "todayPayouts"],
      0,
      Boolean(summary.todays_payouts_paise || summary.todaysPayoutsPaise || summary.today_payouts_paise || summary.todayPayoutsPaise),
    )),
  },
  {
    label: payoutQuickStats[1].label,
    value: formatDashboardMoney(pickNumber(
      summary,
      ["pending_approvals_paise", "pendingApprovalsPaise", "pending_approvals", "pendingApprovals"],
      0,
      Boolean(summary.pending_approvals_paise || summary.pendingApprovalsPaise),
    )),
  },
  {
    label: payoutQuickStats[2].label,
    value: formatDashboardMoney(pickNumber(
      summary,
      ["successful_transfers_paise", "successfulTransfersPaise", "successful_transfers", "successfulTransfers"],
      0,
      Boolean(summary.successful_transfers_paise || summary.successfulTransfersPaise),
    )),
  },
  {
    label: payoutQuickStats[3].label,
    value: pickNumber(
      summary,
      ["total_beneficiaries", "totalBeneficiaries", "beneficiaries_count", "beneficiariesCount"],
      0,
    ).toLocaleString("en-IN"),
  },
];

export const loadDashboardViewData = async (
  signal: AbortSignal,
): Promise<PayoutDashboardViewData> => {
  const [summary, apiAccounts, apiPayouts, beneSummary] = await Promise.all([
    payoutApi.dashboardSummary(signal),
    payoutApi.accounts(signal),
    payoutApi.paymentSearch({ limit: 25 }, signal),
    payoutApi.beneficiariesSummary(signal).catch(() => ({})),
  ]);

  const accounts = apiAccounts.length
    ? await Promise.all(
        apiAccounts.map(async (account, index) => {
          const fallback: PayoutAccount = {
            id: "",
            name: "",
            school: "",
            bank: "",
            logoUrl: "",
            accountNumber: "",
            ifsc: "",
            currency: "INR",
            addedOn: "",
            availableBalance: 0,
            accountBalance: 0,
            fundsOnHold: 0,
            overdraftBalance: 0,
            overdraftLimit: 0,
            lowBalanceThreshold: 0,
            accent: "",
          };
          const mapped = accountFromApi(account, fallback);
          try {
            const balancePayload = await payoutApi.accountBalance(mapped.id, signal);
            const liveBalance = paiseToRupees(availableBalancePaiseFromWire(balancePayload));
            return { ...mapped, availableBalance: liveBalance };
          } catch {
            return mapped;
          }
        }),
      )
    : [];

  const payouts = apiPayouts.length
    ? apiPayouts.map((payment) =>
        payoutFromApi(payment, {
          referenceId: "",
          utrNumber: "",
          beneficiary: "",
          account: "",
          accountId: "",
          purpose: "",
          amount: 0,
          status: "",
          settlementTime: "",
        }),
      )
    : [];

  const availableBalance = pickNumber(
    summary,
    ["total_available_balance_paise", "totalAvailableBalancePaise", "total_available_balance", "totalAvailableBalance"],
    accounts.reduce((sum, account) => sum + account.availableBalance, 0),
    Boolean(summary.total_available_balance_paise || summary.totalAvailableBalancePaise),
  );

  const beneTotal = pickNumber(beneSummary as Record<string, unknown>, ["total"], 0);
  const mergedSummary = {
    ...summary,
    total_beneficiaries:
      summary.total_beneficiaries ??
      summary.totalBeneficiaries ??
      beneTotal,
  };

  return {
    accounts,
    recentPayouts: payouts,
    totalAvailableBalance: availableBalance,
    quickStats: summaryQuickStats(mergedSummary),
  };
};
